import { FormikProvider, useField, useFormik } from 'formik';
import _ from 'lodash';
import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	useErrorBoundary,
	useLocalize,
	useLocalizedTranslation,
	useSupportedLanguageIDs,
	withLocalizedTranslation,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ActionArchiveListItem,
	Button,
	Checkbox,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Paragraph,
	Radio,
	Select,
	SettingsSection,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import DetailsTableForm from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import { cellColorValue } from '../../talent-reviews/v2/talentReviewColors';
import { placementHelpQuestionsAreReady } from '../../talent-reviews/v2/talentReviewPlacementHelp';
import TalentReviewPlacementHelpEditor, {
	placementHelpQuestionsFromTemplate,
	placementHelpQuestionsInput,
} from '../../talent-reviews/v2/templates/TalentReviewPlacementHelpEditor';
import TalentReviewGrid from './TalentReviewGrid';
import TalentReviewNormDistribution from './TalentReviewNormDistribution';
import { TOTAL_NORM, distributeEvenly, totalNorm } from './norms';

function TalentReviewLanguageSelector() {
	const { t } = useTranslation();
	const [{ value: languageID }, , { setValue: setLanguageID }] = useField('languageID');
	const { languageIDs } = useSupportedLanguageIDs();

	const options = _.sortBy(
		languageIDs?.map((id) => ({
			label: t(`common.language.${id}`),
			value: id,
		})) ?? [],
		({ label }) => label,
	);

	return (
		<Box width={200}>
			<Select
				options={options.map(({ value, label }) => ({
					title: label,
					value,
				}))}
				selectedValue={languageID}
				onChange={(value) => setLanguageID(value!)}
				small
			/>
		</Box>
	);
}

const Warning = styled(Box)`
	background: ${(props) => rgba(props.theme.colors.yellow, 0.25)};
	border-radius: 4px;
`;

const talentReviewVisibilityKey: { [key in graphql.TalentReviewVisibility]: string } = {
	[graphql.TalentReviewVisibility.Manager]: 'manager',
	[graphql.TalentReviewVisibility.Members]: 'everyone',
	[graphql.TalentReviewVisibility.Self]: 'self',
};

const TalentReviewEditor = withLocalizedTranslation(function TalentReviewEditor(props: {
	template?: graphql.TalentReviewTemplate;
	localizedT?: ReturnType<typeof useLocalizedTranslation>['localizedT'];
}) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const formik = useFormik({
		initialValues: {
			languageID: language.id,
			title: props.template?.title ?? [],
			description: props.template?.description ?? [],
			visibility: props.template?.visibility ?? graphql.TalentReviewVisibility.Self,
			outdatedAfterDays: props.template?.outdatedAfterDays ?? 30,
			hasNorm: props.template?.hasNorm ?? false,
			xAxis: {
				label:
					props.template?.xAxis.label ??
					props.localizedT!('settings.talentReviews.default.xAxis.label'),
				ticks: props.template?.xAxis.ticks ?? [
					props.localizedT!('settings.talentReviews.default.xAxis.ticks.0'),
					props.localizedT!('settings.talentReviews.default.xAxis.ticks.1'),
					props.localizedT!('settings.talentReviews.default.xAxis.ticks.2'),
				],
			},
			yAxis: {
				label:
					props.template?.yAxis.label ??
					props.localizedT!('settings.talentReviews.default.yAxis.label'),
				ticks: props.template?.yAxis.ticks ?? [
					props.localizedT!('settings.talentReviews.default.yAxis.ticks.0'),
					props.localizedT!('settings.talentReviews.default.yAxis.ticks.1'),
					props.localizedT!('settings.talentReviews.default.yAxis.ticks.2'),
				],
			},
			cells:
				props.template?.cells.map((row) => row.map((cell) => _.omit(cell, '__typename'))) ??
				distributeEvenly(
					Array.from({ length: 3 }, () =>
						Array.from({ length: 3 }, () => ({ label: [], instruction: [] })),
					),
				),
			placementHelpQuestions: placementHelpQuestionsFromTemplate(props.template),
		},
		onSubmit: () => {},
	});

	const [create, { loading: createLoading }] = graphql.useCreateTalentReviewTemplateMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateTalentReviewTemplateMutation();

	const loading = createLoading || updateLoading;

	const history = useHistory();

	const [error, attempt] = useErrorBoundary();

	const balanced = !formik.values.hasNorm || totalNorm(formik.values.cells) === TOTAL_NORM;
	const placementHelpReady = placementHelpQuestionsAreReady(
		formik.values.placementHelpQuestions,
		formik.values.languageID,
	);

	const onSave = attempt(async () => {
		if (!balanced || !placementHelpReady) {
			return;
		}

		const input = {
			title: formik.values.title,
			description: formik.values.description,
			visibility: formik.values.visibility,
			outdatedAfterDays: formik.values.outdatedAfterDays,
			hasNorm: formik.values.hasNorm,
			xAxis: formik.values.xAxis,
			yAxis: formik.values.yAxis,
			// Colours are fixed by position (see cellColors); keep the stored colour in step.
			cells: formik.values.cells.map((row, rowIndex) =>
				row.map((cell, columnIndex) => ({
					...cell,
					color: cellColorValue(rowIndex, columnIndex, formik.values.cells.length, row.length),
				})),
			),
			placementHelpQuestions: placementHelpQuestionsInput(formik.values.placementHelpQuestions),
		};

		if (props.template) {
			await update({ variables: { input: { ...input, templateID: props.template.id } } });
		} else {
			const response = await create({ variables: { input } });

			history.push(
				`/settings/talent-reviews/${response.data!.createTalentReviewTemplate.template.id}`,
			);
		}
	});

	return (
		<FormikProvider value={formik}>
			<Flex flexDirection="row" alignItems="center">
				<Heading>{t(`settings.talentReviews.${props.template ? 'edit' : 'create'}.title`)}</Heading>
			</Flex>
			<Heading3 mt={`${tokens.gap.section}px`} mb={`${tokens.gap.group}px`}>
				{t('settings.talentReviews.edit.details')}
			</Heading3>
			<GraphQLErrorMessage error={error} />
			<DetailsTableForm
				formValues={[
					{
						label: t('settings.talentReviews.edit.name'),
						name: 'title',
						type: DetailsTableFormValueTypes.Multilingual,
					},
					{
						label: t('settings.talentReviews.edit.description'),
						name: 'description',
						type: DetailsTableFormValueTypes.MultilingualQuill,
					},
				]}
			/>

			<SettingsSection title={t('settings.talentReviews.edit.visibility.label')}>
				<Warning p={3} mb={2}>
					<Paragraph color="black">{t('settings.talentReviews.edit.visibility.warning')}</Paragraph>
				</Warning>

				{[
					graphql.TalentReviewVisibility.Members,
					graphql.TalentReviewVisibility.Self,
					graphql.TalentReviewVisibility.Manager,
				].map((visibility) => {
					const translationPrefix = `settings.talentReviews.edit.visibility.${
						talentReviewVisibilityKey[visibility]
					}`;

					return (
						<Radio.Option
							key={visibility}
							checked={formik.values.visibility === visibility}
							onChange={() => formik.setFieldValue('visibility', visibility)}
							title={t(`${translationPrefix}.title`)}
							description={t(`${translationPrefix}.description`)}
							maxWidth={600}
						/>
					);
				})}
			</SettingsSection>

			<SettingsSection
				title={
					<Heading3 mb={0}>
						{formik.values.xAxis.ticks.length * formik.values.yAxis.ticks.length}{' '}
						{t('settings.talentReviews.edit.grid')}
					</Heading3>
				}
				actions={<TalentReviewLanguageSelector />}
			>
				<Checkbox
					checked={formik.values.hasNorm}
					label={t('settings.talentReviews.edit.norm.enabled')}
					onClick={() => formik.setFieldValue('hasNorm', !formik.values.hasNorm)}
				/>

				<Box width="fit-content" pr={3}>
					{formik.values.hasNorm && <TalentReviewNormDistribution />}

					<TalentReviewGrid existing={!!props.template} />
				</Box>
			</SettingsSection>

			<TalentReviewPlacementHelpEditor />

			<Flex mb={3}>
				<Button
					title={t('common.save')}
					onClick={() => onSave()}
					loading={loading}
					disabled={!balanced || !placementHelpReady}
				/>
			</Flex>

			{props.template && (
				<SettingsSection title={t('settings.talentReviews.actions.title')}>
					<ActionArchiveListItem
						archivableItem={props.template}
						archivableItemName={t('settings.talentReviews.archivableItemName')}
						archivableItemKey="jobTitle"
						useArchive={graphql.useArchiveTalentReviewTemplateMutation}
						useRestore={graphql.useRestoreTalentReviewTemplateMutation}
					/>
				</SettingsSection>
			)}
		</FormikProvider>
	);
});

export default function TalentReviewEditorWithID(props: { id?: string }) {
	const query = graphql.useTalentReviewTemplateQuery({
		variables: { id: props.id! },
		skip: !props.id,
	});

	if (!props.id) {
		return <TalentReviewEditor />;
	} else if (query.data) {
		return (
			<TalentReviewEditor
				template={query.data.talentReviewTemplate as graphql.TalentReviewTemplate}
			/>
		);
	} else {
		return <Spinner centered />;
	}
}
